import { ChangeEvent, FormEvent, MouseEvent, useState } from 'react'

import ArrowBack from '@mui/icons-material/ArrowBack'
import ReportGmailerrorred from '@mui/icons-material/ReportGmailerrorred'
import Visibility from '@mui/icons-material/Visibility'
import { Alert, InputAdornment, Stack, Typography } from '@mui/material'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import IconButton from '@mui/material/IconButton'
import Link from '@mui/material/Link'
import TextField from '@mui/material/TextField'
import { DGE_AUTH_METHOD, PLATFORM } from '@/constants/platform'

import type { PasswordInputType } from '../../constants'
import { PASSWORD_INPUT_TYPES } from '../../constants'
import { KeycloakButton } from '../../KeycloakButton'
import { LoginForgotPassActiveForm, useCredentials } from '../../useLogin/useCredentials'
import { LoginStepProps } from '../types'
import { DGEStepData } from './types'

export const DGEAuthStepStrategy = ({ onBack, stepData }: LoginStepProps<DGEStepData>) => {
  const { signIn, onActiveFormUpdate } = useCredentials()
  const [password, setPassword] = useState<string>('')
  const [passwordInputType, setPasswordInputType] = useState<PasswordInputType>(
    PASSWORD_INPUT_TYPES.PASSWORD
  )
  const [error, setError] = useState<Error>()
  const [isLoading, setIsLoading] = useState(false)

  const email = stepData?.email ?? ''
  const authMethod = stepData?.authMethod
  const isKeycloakAuth = authMethod === DGE_AUTH_METHOD.KEYCLOAK
  const isLegacyAuth = authMethod === DGE_AUTH_METHOD.LEGACY

  const onPasswordChange = (event: ChangeEvent<HTMLInputElement>) => {
    setPassword(event.target.value)
  }

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!password) return

    setError(undefined)
    setIsLoading(true)

    try {
      await signIn(email, password, PLATFORM.DGE, DGE_AUTH_METHOD.LEGACY)
    } catch (e) {
      setError(e as Error)
    } finally {
      setIsLoading(false)
    }
  }

  const onMouseDown = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    setPasswordInputType(PASSWORD_INPUT_TYPES.TEXT)
  }

  const onMouseUp = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    setPasswordInputType(PASSWORD_INPUT_TYPES.PASSWORD)
  }

  const onForgotPassword = () => {
    onActiveFormUpdate(LoginForgotPassActiveForm.FORGOT_PASSWORD)
  }

  return (
    <Stack>
      {error && (
        <Alert variant={'outlined'} color={'error'} sx={{ mb: 3 }} icon={<ReportGmailerrorred />}>
          {error.message}
        </Alert>
      )}

      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <IconButton onClick={onBack} size="small" sx={{ ml: -1 }}>
          <ArrowBack />
        </IconButton>
        <Typography variant="body2" color="text.secondary">
          {email}
        </Typography>
      </Stack>

      {isLegacyAuth && (
        <form onSubmit={onSubmit}>
          <Stack
            sx={{
              '& .MuiFormControl-root': {
                backgroundColor: '#f2f2f2'
              }
            }}
          >
            <TextField
              required
              value={password}
              onChange={onPasswordChange}
              type={passwordInputType}
              label={'Password'}
              fullWidth
              autoFocus
              InputProps={{
                endAdornment: (
                  <InputAdornment position={'end'}>
                    <IconButton edge={'end'} onMouseDown={onMouseDown} onMouseUp={onMouseUp}>
                      <Visibility />
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
            <Link
              href={'#'}
              variant="body2"
              underline={'hover'}
              style={{ textAlign: 'right' }}
              onClick={onForgotPassword}
            >
              Forgot password
            </Link>
            <Button
              type={'submit'}
              variant={'contained'}
              color={'primary'}
              sx={{ mt: 4 }}
              disabled={isLoading || !password}
              startIcon={isLoading && <CircularProgress size={24} />}
            >
              Submit
            </Button>
          </Stack>
        </form>
      )}

      {isKeycloakAuth && (
        <Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Your account uses Single Sign-On (SSO) authentication.
          </Typography>
          <KeycloakButton fullWidth />
        </Stack>
      )}
    </Stack>
  )
}
